import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { FormInputs } from "./form-validation/user-login.schema";
import { formatFormDataToBodyData } from "./form-validation/user-login.schema";
import type { FormInputs as ResetPasswordFormInputs } from "./form-validation/user-reset-password.schema";
import { formatFormDataToBodyData as ResetPasswordFormatFormDataToBodyData } from "./form-validation/user-reset-password.schema";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { loginUser } from "../../../write/application/use-cases/user-login/login-user";
import { resetLoginUserState } from "../../../write/application/use-cases/user-login/login-user-reducer";
import { selectUserLogin } from "@user-management-context/write/application/use-cases/user-login/selectors/user-login-selectors";
import LoginForm from "./login-form";
import ResetForm from "./reset-form";
import CheckPage from "./check-page";
import { resetUserPassword } from "../../../write/application/use-cases/user-password-reset/reset-user-password";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import { resolveLandingUrl } from "@user-management-context/shared/domain/landing-url";

type STEP = "login" | "reset" | "check";
interface LocationState {
  from?: string;
}

const LoginPage = () => {
  const [step, setStep] = useState<STEP>("login");
  const [email, setEmail] = useState("");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  // Read only: the toast for this outcome is raised by App, which outlives the
  // redirect that unmounts this page on success.
  const { processing } = useAppSelector(selectUserLogin);
  const { user, activeRole } = useSession();

  useEffect(() => {
    if (processing !== "success" || !user) return;
    const redirect = (state as Nullable<LocationState>)?.from ?? null;
    const target = resolveLandingUrl({ activeRole, isAdmin: user.isAdmin, redirect });
    if (target) navigate(target);
  }, [processing, navigate, state, user, activeRole]);

  useEffect(() => {
    return () => {
      dispatch(resetLoginUserState());
    };
  }, [dispatch]);

  const onLogin = (formBody: FormInputs) => {
    const body = formatFormDataToBodyData(formBody);
    dispatch(loginUser(body));
  };

  const onForgetPassword = () => setStep("reset");
  const onPasswordReset = (formBody: ResetPasswordFormInputs) => {
    const body = ResetPasswordFormatFormDataToBodyData(formBody);
    dispatch(resetUserPassword(body));
    setEmail(formBody.email);
    setStep("check");
  };
  const onBackToLogin = () => setStep("login");

  return (
    <div className="flex items-center justify-center">
      {step === "login" && <LoginForm processing={processing} onSubmit={onLogin} onDisplayResetPasswordForm={onForgetPassword} />}
      {step === "reset" && <ResetForm onSubmit={onPasswordReset} onBack={onBackToLogin} />}
      {step === "check" && <CheckPage onBack={onBackToLogin} email={email} />}
    </div>
  );
};

export default LoginPage;
